import Commons from '../../../../Services/Common/Common';

/**
 * SAM_BASIC_02 (안전작업허가서) 스키마
 * 목록 그리드 행: camelCase (pmCd, reqNm, partnerNm, workDate, status …)
 * API payload 키: 서버 규약(PmCd 등)은 mapper에서 암호화해 전송
 */
export const SAM_BASIC_02_Schema = {
  fields: {
    // 그리드/폼 필드: camelCase
    pmCd: { initial: '', label: '작업허가서코드' },
    reqNm: { initial: '', label: '성명' },
    partnerNm: { initial: '', label: '공사업체명' },
    workContent: { initial: '', label: '작업내용' },
    strTime: { initial: '', label: '작업시작일자' },
    endTime: { initial: '', label: '작업종료일자' },
    totalStartDate: { initial: '', label: '작업시작일자(MST)' },
    totalEndDate: { initial: '', label: '작업종료일자(MST)' },
    status: { initial: '', label: '처리상태' },
    createDate: { initial: null, label: '작성일자' },
    workDate: { initial: null, label: '작업일자' },
    depCd: { initial: '', label: '부서코드' },
    userId: { initial: '', label: '사용자ID' },
  },

  mapper: {
    // 작업허가서 목록 (GetSamBasic02R4 — PM_CD 1행, TOTAL_START/END_DATE 기간 겹침)
    search1: {
      UserId: (_, context) =>
        context.userInfo?.userid || context.userInfo?.userId || '',
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      StartDate: (_, context) =>
        Commons.EncryptAES(
          context.search?.workStartDate || context.search?.startDate || ''
        ) || '',
      EndDate: (_, context) =>
        Commons.EncryptAES(
          context.search?.workEndDate || context.search?.endDate || ''
        ) || '',
      DepCd: (_, context) =>
        Commons.EncryptAES(context.search?.depCd || '') || '',
      /** 처리상태 필터; 전체 조회 시 빈 문자열(암호화) */
      Status: (_, context) =>
        Commons.EncryptAES(
          context.search?.status || context.search?.gubun || ''
        ) || '',
    },

    // 허가 상세 (api/SamBasic/GetSamBasic02R5 — [FromQuery] PmCd, WorkDate, UserId, 모두 AES)
    search2: {
      PmCd: (_, context) =>
        Commons.EncryptAES(
          String(context?.row?.pmCd ?? context?.row?.PmCd ?? '').trim()
        ) || '',
      WorkDate: (_, context) => {
        const raw =
          context?.row?.workDate ??
          context?.row?.WorkDate ??
          context?.row?.createDate ??
          context?.row?.CreateDate ??
          '';
        const ymd = raw ? String(raw).trim().substring(0, 10) : '';
        return Commons.EncryptAES(ymd) || '';
      },
      UserId: (_, context) =>
        context.userInfo?.userid || context.userInfo?.userId || '',
    },

    // 안전작업허가서 반려이력 조회
    search3: {
      PmCd: (_, context) =>
        Commons.EncryptAES(context?.row?.pmCd ?? context?.row?.PmCd ?? '') ||
        '',
    },

    search4: {
      CustCd: (_, context) => Commons.EncryptAES(context.userInfo?.custCd),
    },

    /** GetSamBasic02R2Catalog — [FromQuery] custCd (AES) */
    search5: {
      custCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
    },

    /**
     * SamBasic SaveSamBasic02U2 — 서버에서 필드별 DecryptAES
     * GUBUN: '0' 반려 | '1' 중간결재(센터) | '2' 최종(SM)
     */
    saveSamBasic02U2: {
      Gubun: (row) => Commons.EncryptAES(row.gubun || '') || '',
      PmCd: (row) => Commons.EncryptAES(row.pmCd ?? row.PmCd ?? '') || '',
      DailyCd: (row) =>
        Commons.EncryptAES(
          String(row.dailyCd ?? row.DailyCd ?? row.DAILY_CD ?? '').trim()
        ) || '',
      WorkDate: (row) => {
        const raw =
          row.workDate ??
          row.WorkDate ??
          row.createDate ??
          row.CreateDate ??
          '';
        const ymd = raw ? String(raw).trim().substring(0, 10) : '';
        return Commons.EncryptAES(ymd) || '';
      },
      /** UserId는 암호화하지 않음(서버에서 평문 또는 토큰 그대로 처리) */
      UserId: (_, context) =>
        context.userInfo?.userid || context.userInfo?.userId || '',
      ReturnDetail: (row) =>
        Commons.EncryptAES(row.returnDetail ?? row.ReturnDetail ?? '') || '',
      /** 반려(GUBUN=0) 시 TB_BO_PERMIT_RETURN.DTL_CD — 없으면 '0' 등 서버 허용값 */
      DtlCd: (row) =>
        Commons.EncryptAES(
          String(row.dtlCd ?? row.DtlCd ?? row.DTL_CD ?? '').trim() || '0'
        ) || '',
      CenterId: (row) =>
        Commons.EncryptAES(
          String(
            row.centerId ??
              row.CenterId ??
              row.CENTER_ID ??
              row.depHeadId ??
              row.DepHeadId ??
              ''
          ).trim() || ''
        ) || '',
      CenterSignPath: (row) =>
        Commons.EncryptAES(
          row.centerSignPath ??
            row.CenterSignPath ??
            row.depSignPath ??
            row.DepSignPath ??
            ''
        ) || '',
      SmSignPath: (row) =>
        Commons.EncryptAES(row.smSignPath ?? row.SmSignPath ?? '') || '',
      /** (호환) 구 결재승인자 필드 — 서버에서 CenterId/CenterSignPath로 매핑 */
      DepHeadId: (row) =>
        Commons.EncryptAES(
          String(
            row.depHeadId ?? row.DepHeadId ?? row.centerId ?? row.CenterId ?? ''
          ).trim() || ''
        ) || '',
      DepSignPath: (row) =>
        Commons.EncryptAES(
          row.depSignPath ??
            row.DepSignPath ??
            row.centerSignPath ??
            row.CenterSignPath ??
            ''
        ) || '',
    },
    save3: {
      smId: (row) => row.userInfo?.userid || '',
      gubun: (row) => Commons.EncryptAES(String(row.gubun ?? '')) || '',
      smSignPath: (row) =>
        Commons.EncryptAES(String(row.smSignPath ?? '')) || '',
      returnReason: (row) =>
        Commons.EncryptAES(String(row.returnReason ?? '')) || '',
      dtlCds: (row) =>
        (row.dtlCds || []).map((cd) => Commons.EncryptAES(String(cd ?? ''))),
    },

    // DeleteSamBasic02D1 — [FromBody] PmCd (AES). 작업허가서 전체 삭제
    delete1: {
      PmCd: (row) =>
        Commons.EncryptAES(row.pmCd ?? row.PmCd ?? row.PM_CD ?? '') || '',
    },

    // DeleteSamBasic02D2 — [FromBody] DailyCd (AES) 필수, DtlCd 선택(일자별 삭제 시 생략)
    delete2: {
      DtlCd: (row) => {
        const dtl = String(row.dtlCd ?? row.DtlCd ?? row.DTL_CD ?? '').trim();
        return dtl ? Commons.EncryptAES(dtl) : '';
      },
      DailyCd: (row) =>
        Commons.EncryptAES(
          String(row.dailyCd ?? row.DailyCd ?? row.DAILY_CD ?? '').trim()
        ) || '',
    },
  },
};
